import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './PublicOfferPage.css';

function PublicOfferPage() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    offerAmount: '',
    earnestMoney: '',
    offerExpiration: '',
    financingType: 'conventional',
    downPaymentPercent: '20',
    purchaseContract: null,
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    hasBuyerAgent: false,
    buyerAgentName: '',
    buyerAgentEmail: '',
    buyerAgentPhone: '',
    buyerAgentLicense: '',
    buyerAgentBrokerage: '',
    message: ''
  });

  useEffect(() => {
    fetchListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`/api/public/listing/${slug}`);
      setListing(response.data.listing);
      setFormData(prev => ({
        ...prev,
        offerAmount: response.data.listing.price?.toString() || '',
        earnestMoney: Math.round(response.data.listing.price * 0.01).toString()
      }));
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setError('Failed to load listing. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getDefaultExpirationDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 3);
    return date.toISOString().split('T')[0];
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
    }));
  };

  const formatCurrency = (value) => {
    const num = parseInt(value?.toString().replace(/[^0-9]/g, ''));
    if (isNaN(num)) return '';
    return '$' + num.toLocaleString();
  };

  const formatPercent = (value) => {
    const num = value?.toString().replace(/[^0-9.]/g, '');
    if (!num) return '';
    return num + '%';
  };

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({ ...prev, [name]: numericValue }));
  };

  const handlePercentChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/[^0-9.]/g, '');
    setFormData(prev => ({ ...prev, [name]: numericValue }));
  };

  const handlePhoneChange = (e) => {
    let val = e.target.value.replace(/[^0-9]/g, '');
    if (val.length > 10) val = val.slice(0, 10);
    if (val.length >= 7) {
      val = '(' + val.slice(0, 3) + ') ' + val.slice(3, 6) + '-' + val.slice(6);
    } else if (val.length >= 4) {
      val = '(' + val.slice(0, 3) + ') ' + val.slice(3);
    } else if (val.length >= 1) {
      val = '(' + val;
    }
    setFormData(prev => ({ ...prev, [e.target.name]: val }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.offerAmount || parseInt(formData.offerAmount) <= 0) {
          setError('Please enter a valid offer amount');
          return false;
        }
        if (formData.financingType !== 'cash' && !formData.downPaymentPercent) {
          setError('Please enter a down payment percentage');
          return false;
        }
        if (!formData.earnestMoney || parseInt(formData.earnestMoney) <= 0) {
          setError('Please enter earnest money amount');
          return false;
        }
        if (!formData.offerExpiration || formData.offerExpiration.length < 10) {
          setError('Please enter an offer expiration date (MM/DD/YYYY)');
          return false;
        }
        break;
      case 2:
        if (!formData.buyerName.trim()) {
          setError('Please enter your full name');
          return false;
        }
        if (!formData.buyerEmail.trim() || !/\S+@\S+\.\S+/.test(formData.buyerEmail)) {
          setError('Please enter a valid email address');
          return false;
        }
        if (!formData.buyerPhone.trim()) {
          setError('Please enter your phone number');
          return false;
        }
        break;
      default:
        break;
    }
    setError('');
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    setSubmitting(true);
    setError('');

    try {
      const submitData = new FormData();
      submitData.append('offer_amount', formData.offerAmount);
      submitData.append('earnest_money', formData.earnestMoney);
      submitData.append('offer_expiration', formData.offerExpiration || getDefaultExpirationDate());
      submitData.append('financing_type', formData.financingType);
      submitData.append('down_payment_percent', formData.downPaymentPercent);
      submitData.append('buyer_name', formData.buyerName);
      submitData.append('buyer_email', formData.buyerEmail);
      submitData.append('buyer_phone', formData.buyerPhone);
      submitData.append('has_buyer_agent', formData.hasBuyerAgent);
      submitData.append('message', formData.message);
      submitData.append('closing_date', '');
      submitData.append('option_fee', '0');
      submitData.append('option_days', '0');

      if (formData.hasBuyerAgent) {
        submitData.append('buyer_agent_name', formData.buyerAgentName);
        submitData.append('buyer_agent_email', formData.buyerAgentEmail);
        submitData.append('buyer_agent_phone', formData.buyerAgentPhone);
        submitData.append('buyer_agent_license', formData.buyerAgentLicense);
        submitData.append('buyer_agent_brokerage', formData.buyerAgentBrokerage);
      }

      if (formData.purchaseContract) {
        submitData.append('purchase_contract', formData.purchaseContract);
      }

      await axios.post(`/api/public/offer/${slug}`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit offer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="public-offer-page"></div>;
  }

  if (notFound) {
    return (
      <div className="public-offer-page">
        <div className="public-offer-not-found">
          <div className="not-found-icon">🏠</div>
          <h2>Listing Not Found</h2>
          <p>This property may no longer be available or the link may be incorrect.</p>
          <p className="not-found-hint">If you believe this is an error, please contact the seller directly.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="public-offer-page">
        <div className="public-offer-success-card">
          <div className="success-icon">✓</div>
          <h2>Offer Submitted!</h2>
          <p className="success-summary">
            Your offer of <strong>${parseInt(formData.offerAmount).toLocaleString()}</strong> for{' '}
            <strong>{listing.address}, {listing.city}, {listing.state}</strong> has been sent to the seller.
          </p>
          <div className="success-details">
            <div className="success-detail-row">
              <span>A confirmation has been sent to <strong>{formData.buyerEmail}</strong></span>
            </div>
            <div className="success-detail-row">
              <span>The seller will review your offer and respond shortly</span>
            </div>
          </div>
          <p className="success-note">
            This is a non-binding offer. If accepted, you'll work with the seller to execute a formal purchase contract.
          </p>
          <div className="powered-by">
            Powered by <a href="https://liberastro.com" target="_blank" rel="noopener noreferrer">Liber Astro</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-offer-page">
      <div className="public-offer-header">
        <div className="property-hero">
          {listing.primary_image && (
            <div className="property-image">
              <img src={listing.primary_image} alt={listing.address} />
            </div>
          )}
          <div className="property-details">
            <p className="property-price">${listing.price?.toLocaleString()}</p>
            <h1>{listing.address}</h1>
            <p className="property-location">{listing.city}, {listing.state} {listing.zip_code}</p>
            <p className="property-stats-text">
              {[
                listing.bedrooms && `${listing.bedrooms} bed`,
                listing.bathrooms && `${listing.bathrooms} bath`,
                listing.square_feet && `${listing.square_feet.toLocaleString()} sqft`,
                listing.year_built && `Built ${listing.year_built}`
              ].filter(Boolean).join('  ·  ')}
            </p>
          </div>
        </div>
      </div>

      <div className="public-offer-form-container">
        <h2>Submit Your Offer</h2>
        <p className="po-form-subtitle">No account required.</p>

        <div className="po-breadcrumb">
          {['Offer Details', 'Your Info', 'Review'].map((step, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span className="po-breadcrumb-sep">›</span>}
              <span
                className={`po-breadcrumb-item ${currentStep === idx + 1 ? 'active' : ''} ${currentStep > idx + 1 ? 'completed' : ''}`}
              >
                {currentStep > idx + 1 && '✓ '}{step}
              </span>
            </React.Fragment>
          ))}
        </div>

        {error && <div className="po-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {currentStep === 1 && (
            <div className="po-form-section">
              <h3>Offer Details</h3>

              <div className="po-form-group">
                <label>Purchase Price <span className="po-required">*</span></label>
                <input
                  type="text"
                  name="offerAmount"
                  value={formatCurrency(formData.offerAmount)}
                  onChange={handleCurrencyChange}
                  placeholder="0"
                />
                {listing && formData.offerAmount && (
                  <p className="po-input-hint">
                    {((parseInt(formData.offerAmount) / listing.price) * 100).toFixed(1)}% of asking price
                  </p>
                )}
              </div>

              {formData.financingType !== 'cash' && (
                <div className="po-form-group">
                  <label>Down Payment <span className="po-required">*</span></label>
                  <input
                    type="text"
                    name="downPaymentPercent"
                    value={formatPercent(formData.downPaymentPercent)}
                    onChange={handlePercentChange}
                    placeholder="20%"
                  />
                  {formData.offerAmount && formData.downPaymentPercent && (
                    <p className="po-input-hint">
                      ${Math.round(parseInt(formData.offerAmount) * (parseFloat(formData.downPaymentPercent) / 100)).toLocaleString()} down payment
                    </p>
                  )}
                </div>
              )}

              <div className="po-form-group">
                <label>Earnest Money <span className="po-required">*</span></label>
                <input
                  type="text"
                  name="earnestMoney"
                  value={formatCurrency(formData.earnestMoney)}
                  onChange={handleCurrencyChange}
                  placeholder="0"
                />
                <p className="po-input-hint">Typically 1-2% of purchase price</p>
              </div>

              <div className="po-form-group">
                <label>Offer Expiration <span className="po-required">*</span></label>
                <input
                  type="text"
                  name="offerExpiration"
                  value={formData.offerExpiration}
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^0-9]/g, '');
                    if (val.length > 8) val = val.slice(0, 8);
                    if (val.length >= 5) {
                      val = val.slice(0, 2) + '/' + val.slice(2, 4) + '/' + val.slice(4);
                    } else if (val.length >= 3) {
                      val = val.slice(0, 2) + '/' + val.slice(2);
                    }
                    setFormData(prev => ({ ...prev, offerExpiration: val }));
                  }}
                  placeholder="MM/DD/YYYY"
                  maxLength={10}
                />
                <p className="po-input-hint">Default: 3 days from today</p>
              </div>

              <div className="po-form-group">
                <label>Financing Type <span className="po-required">*</span></label>
                <select
                  name="financingType"
                  value={formData.financingType}
                  onChange={handleChange}
                >
                  <option value="conventional">Conventional</option>
                  <option value="cash">Cash</option>
                  <option value="fha">FHA</option>
                  <option value="va">VA</option>
                  <option value="usda">USDA</option>
                </select>
              </div>

              <div className="po-form-group">
                <label>Purchase Contract</label>
                <div
                  className="po-file-upload"
                  onClick={() => document.getElementById('po-file-input').click()}
                >
                  <input
                    id="po-file-input"
                    type="file"
                    name="purchaseContract"
                    onChange={handleChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                  />
                  {formData.purchaseContract ? (
                    <div className="po-file-selected">
                      <span className="po-file-name">
                        {formData.purchaseContract.name}
                      </span>
                      <button
                        type="button"
                        className="po-file-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, purchaseContract: null }));
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="po-file-placeholder">
                      <span>Click to upload</span>
                    </div>
                  )}
                </div>
                <p className="po-input-hint">PDF, JPG, or PNG accepted</p>
              </div>

            </div>
          )}

          {currentStep === 2 && (
            <div className="po-form-section">
              <h3>Your Information</h3>

              <div className="po-form-group">
                <label>Full Legal Name <span className="po-required">*</span></label>
                <input
                  type="text"
                  name="buyerName"
                  value={formData.buyerName}
                  onChange={handleChange}
                  placeholder="Enter your full legal name"
                />
              </div>

              <div className="po-form-row">
                <div className="po-form-group">
                  <label>Email <span className="po-required">*</span></label>
                  <input
                    type="email"
                    name="buyerEmail"
                    value={formData.buyerEmail}
                    onChange={handleChange}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="po-form-group">
                  <label>Phone <span className="po-required">*</span></label>
                  <input
                    type="tel"
                    name="buyerPhone"
                    value={formData.buyerPhone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 555-5555"
                    maxLength={14}
                  />
                </div>
              </div>

              <div className="po-form-group">
                <label>Message to Seller (Optional)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Introduce yourself or share why you're interested in this property..."
                  rows={3}
                />
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="po-form-section">
              <h3>Review Your Offer</h3>

              <div className="po-review-section">
                <h4>Offer Terms</h4>
                <div className="po-review-grid">
                  <div className="po-review-item">
                    <span className="po-review-label">Purchase Price</span>
                    <span className="po-review-value">${parseInt(formData.offerAmount).toLocaleString()}</span>
                  </div>
                  <div className="po-review-item">
                    <span className="po-review-label">Earnest Money</span>
                    <span className="po-review-value">${parseInt(formData.earnestMoney).toLocaleString()}</span>
                  </div>
                  <div className="po-review-item">
                    <span className="po-review-label">Financing</span>
                    <span className="po-review-value" style={{ textTransform: 'capitalize' }}>
                      {formData.financingType.replace('_', ' ')}
                    </span>
                  </div>
                  {formData.financingType !== 'cash' && (
                    <div className="po-review-item">
                      <span className="po-review-label">Down Payment</span>
                      <span className="po-review-value">{formData.downPaymentPercent}%</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="po-review-section">
                <h4>Buyer</h4>
                <div className="po-review-grid">
                  <div className="po-review-item">
                    <span className="po-review-label">Name</span>
                    <span className="po-review-value">{formData.buyerName}</span>
                  </div>
                  <div className="po-review-item">
                    <span className="po-review-label">Email</span>
                    <span className="po-review-value">{formData.buyerEmail}</span>
                  </div>
                  <div className="po-review-item">
                    <span className="po-review-label">Phone</span>
                    <span className="po-review-value">{formData.buyerPhone}</span>
                  </div>
                </div>
              </div>

              {formData.message && (
                <div className="po-review-section">
                  <h4>Message</h4>
                  <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>{formData.message}</p>
                </div>
              )}

              <div className="po-disclaimer">
                <p><strong>By submitting this offer, you acknowledge:</strong></p>
                <ul>
                  <li>This is a non-binding offer until a purchase contract is fully executed by all parties</li>
                  <li>You will need to sign the official purchase contract if your offer is accepted</li>
                  <li>Earnest money will be due per the terms of the executed contract</li>
                </ul>
              </div>
            </div>
          )}

          <div className="po-form-actions">
            {currentStep > 1 && (
              <button type="button" className="po-btn-back" onClick={prevStep}>
                Back
              </button>
            )}
            <div className="po-actions-right">
              {currentStep < 3 ? (
                <button type="button" className="po-btn-next" onClick={nextStep}>
                  Continue
                </button>
              ) : (
                <button type="submit" className="po-btn-submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Offer'}
                </button>
              )}
            </div>
          </div>
        </form>

        <div className="powered-by">
          Powered by <a href="https://liberastro.com" target="_blank" rel="noopener noreferrer">Liber Astro</a>
        </div>
      </div>
    </div>
  );
}

export default PublicOfferPage;
